import * as THREE from 'three';
import {
  TileCoordinate,
  TileInfo,
  LODLevel,
  CameraViewport,
  TileManagerConfig,
  onLODChangeCallback,
  CubeFace,
} from '../Types';

// Import all the modular services
import { DEFAULT_LOD_LEVELS, RENDERING, TIMING, LOADING } from './constants';
import { TextureCache } from './TextureCache';
import { SpatialIndex } from './SpatialIndex';
import { SceneAdapter } from './SceneAdapter';
import { TileLoadQueue } from './TileLoadQueue';
import { LODController } from './LODController';
import { ViewportService } from './ViewportService';
import { ReplacementPolicy } from './ReplacementPolicy';
import { TransitionCoordinator } from './TransitionCoordinator';
import { Debug, DebugConfig } from './Debug';
import { UrlBuilder } from './UrlBuilder';

/**
 * TileManager facade that preserves the original API while delegating to modular services
 */
export class TileManager {
  // Core services
  private readonly textureCache: TextureCache;
  private readonly spatialIndex: SpatialIndex;
  private readonly sceneAdapter: SceneAdapter;
  private readonly loadQueue: TileLoadQueue;
  private readonly lodController: LODController;
  private readonly viewportService: ViewportService;
  private readonly replacementPolicy: ReplacementPolicy;
  private readonly transitionCoordinator: TransitionCoordinator;
  private readonly debug: Debug;

  // Configuration
  private config: TileManagerConfig;
  private isInitialized = false;
  private currentSourceMode: 'standard' | 'blurred' = 'standard';

  // Loading state tracking
  private isLoadingTextures = false;
  private loadingStartTime = 0;
  private pendingLoads = 0;
  private isLoadingBaseTiles = false;
  private readonly baseLoadingCallbacks = new Set<(isLoading: boolean) => void>();
  private anyLoading = false;
  private readonly anyLoadingCallbacks = new Set<(isLoading: boolean) => void>();
  private baseTileTracker: Map<string, boolean> | null = null;
  private baseTilesResolve?: () => void;
  private baseTilesPromise?: Promise<void>;

  constructor(
    scene: THREE.Scene,
    workerUrl: string = 'assets/js/textureWorker.js', // Kept for API compatibility
    config?: Partial<TileManagerConfig>
  ) {
    // Setup default configuration
    this.config = {
      metadata: config?.metadata || {
        url: '',
        levels: [],
      },
      lodLevels: DEFAULT_LOD_LEVELS,
      maxCacheSize: 100,
      tilePadding: RENDERING.DEFAULT_TILE_PADDING,
      transitionBuffer: RENDERING.DEFAULT_TRANSITION_BUFFER,
      ...config,
    };

    // Initialize services
    this.textureCache = new TextureCache(this.config.maxCacheSize || 100);
    this.spatialIndex = new SpatialIndex();
    this.sceneAdapter = new SceneAdapter(scene, this.textureCache, this.spatialIndex, this.config.lodLevels);
    this.loadQueue = new TileLoadQueue(this.textureCache, this.spatialIndex);
    this.lodController = new LODController(this.config.lodLevels);
    this.viewportService = new ViewportService(TIMING.VIEWPORT_UPDATE_THROTTLE, this.config.tilePadding);
    this.replacementPolicy = new ReplacementPolicy(this.spatialIndex);
    this.transitionCoordinator = new TransitionCoordinator(
      this.lodController,
      this.viewportService,
      this.sceneAdapter,
      this.replacementPolicy,
      this.spatialIndex
    );
    this.debug = new Debug(
      this.spatialIndex,
      this.textureCache,
      this.loadQueue,
      this.lodController,
      this.viewportService,
      this.transitionCoordinator,
      this.sceneAdapter
    );
    this.loadQueue.on('tileLoaded', this.handleTileLoaded);
    this.loadQueue.on('tileLoadFailed', this.handleTileLoadFailed);
    this.loadQueue.on('loadCancelled', this.handleTileLoadCancelled);

    // Wire up event handlers
    this.setupEventHandlers();
  }

  /**
   * Setup event handlers between services
   */
  private setupEventHandlers(): void {
    // Handle tile loading start
    this.loadQueue.on('loadStarted', (tileInfo) => {
      this.pendingLoads++;
      this.updateLoadingState();
    });

    // Handle tile loading completion
    this.loadQueue.on('tileLoaded', async (tileInfo, texture) => {
      try {
        // Ensure texture is fully loaded
        await this.sceneAdapter.ensureTextureFullyLoaded(texture);

        // Create mesh and add to scene
        const mesh = await this.sceneAdapter.createTileMesh(tileInfo, texture, tileInfo.url);

        // Notify transition coordinator
        await this.transitionCoordinator.handleTileLoaded(tileInfo, mesh);

        // Update loading state
        this.pendingLoads = Math.max(0, this.pendingLoads - 1);
        this.updateLoadingState();
      } catch (error) {
        this.pendingLoads = Math.max(0, this.pendingLoads - 1);
        this.updateLoadingState();
      }
    });

    // Handle tile loading failures
    this.loadQueue.on('tileLoadFailed', (tileInfo, error) => {
      this.pendingLoads = Math.max(0, this.pendingLoads - 1);
      this.updateLoadingState();
    });

    // Handle load cancellations
    this.loadQueue.on('loadCancelled', (tileInfo) => {
      this.pendingLoads = Math.max(0, this.pendingLoads - 1);
      this.updateLoadingState();
    });

    // Handle viewport changes
    this.viewportService.on('viewportChanged', (event) => {
      // Viewport changed
    });

    // Handle LOD changes
    this.lodController.on('lodChanged', (event) => {
      // Do not cancel in-flight loads; let them finish to avoid black gaps.
      // New requests will still be enqueued with appropriate priorities.
      // Loading state will naturally settle as in-flight operations complete.
    });
  }

  /**
   * Update the TileManager configuration with metadata
   */
  public updateMetadataConfig(
    metadata: TileManagerConfig['metadata'],
    options?: { sourceMode?: 'standard' | 'blurred' },
  ): void {
    this.config.metadata = metadata;
    if (options?.sourceMode) {
      this.currentSourceMode = options.sourceMode;
    } else if (metadata?.sourceMode) {
      this.currentSourceMode = metadata.sourceMode;
    }
  }

  /**
   * Get a stable source key representing the current tile source (e.g., metadata URL)
   */
  public getSourceKey(): string {
    return this.config?.metadata?.url || '';
  }

  /**
   * Expose metadata config (read-only use-case)
   */
  public getMetadataConfig(): TileManagerConfig['metadata'] {
    return this.config?.metadata;
  }

  public getSourceMode(): 'standard' | 'blurred' {
    return this.currentSourceMode;
  }

  public getTileMeshes(): THREE.Mesh[] {
    return this.sceneAdapter.getAllTileMeshes();
  }

  /**
   * Get current LOD level number
   */
  public getCurrentLODLevel(): { level: number; tilesPerFace: number } {
    const lvl = this.lodController.getCurrentLODLevel();
    return { level: lvl.level, tilesPerFace: lvl.tilesPerFace };
  }

  /**
   * Force set LOD level
   */
  public setLODLevel(level: number): boolean {
    return this.lodController.setLODLevel(level);
  }


  /**
   * Set LOD levels configuration dynamically
   * This allows updating LOD levels based on available metadata
   */
  public setLODLevels(lodLevels: LODLevel[]): void {
    if (!lodLevels || lodLevels.length === 0) {
      throw new Error('LOD levels configuration cannot be empty');
    }

    // Update configuration
    this.config.lodLevels = lodLevels;

    // Update LOD controller with new levels
    this.lodController.setLODLevels(lodLevels);

    // Update scene adapter with new LOD levels
    if (this.sceneAdapter) {
      this.sceneAdapter.updateLODLevels(lodLevels);
    }

    // Reset initialization state to trigger reinit with new levels
    this.isInitialized = false;
  }

  /**
   * Preload all 6 cube faces at level 1 (base level)
   */
  private async initializeBaseTiles(): Promise<void> {
    if (!this.config.metadata || !this.config.metadata.url || this.config.metadata.levels.length === 0) {
      throw new Error('Cannot initialize base tiles without metadata configuration.');
    }

    const baseLOD = this.config.lodLevels[0]; // Level 1 (1x1 tiles per face)
    const allFaces = [CubeFace.FRONT, CubeFace.BACK, CubeFace.LEFT, CubeFace.RIGHT, CubeFace.UP, CubeFace.DOWN];

    const baseTileInfos: TileInfo[] = [];

    for (const face of allFaces) {
      for (let x = 0; x < baseLOD.tilesPerFace; x++) {
        for (let y = 0; y < baseLOD.tilesPerFace; y++) {
          const coordinate: TileCoordinate = { x, y, face, level: baseLOD.level };
          baseTileInfos.push({
            ...coordinate,
            url: UrlBuilder.build(coordinate, this.config.metadata, this.config.lodLevels),
            priority: 10, // High priority for base tiles
            isVisible: true,
            distance: 0,
          });
        }
      }
    }

    this.beginBaseTileTracking(baseTileInfos);

    const baseLoadPromises = baseTileInfos.map((tileInfo) => this.loadBaseTile(tileInfo));

    try {
      await Promise.allSettled(baseLoadPromises);
      await this.waitForBaseTilesCompletion();
      this.isInitialized = true;
    } catch (error) {
      // Silently handle initialization errors
    } finally {
      this.resolveBaseTileTracking(true);
    }
  }

  /**
   * Load a single base tile
   */
  private beginBaseTileTracking(tileInfos: TileInfo[]): void {
    this.resolveBaseTileTracking(true);

    const tracker = new Map<string, boolean>();

    for (const tileInfo of tileInfos) {
      const tileKey = UrlBuilder.generateTileKey(tileInfo);
      if (tracker.has(tileKey)) {
        continue;
      }
      const alreadyLoaded =
        this.spatialIndex.hasTile(tileKey) || this.textureCache.has(tileInfo.url);
      tracker.set(tileKey, alreadyLoaded);
    }

    this.baseTileTracker = tracker;

    if (tracker.size === 0) {
      this.isLoadingBaseTiles = true;
      this.updateLoadingState();
      this.resolveBaseTileTracking(true);
      return;
    }

    this.baseTilesPromise = new Promise<void>((resolve) => {
      this.baseTilesResolve = resolve;
    });
    this.isLoadingBaseTiles = true;
    this.updateLoadingState();
    this.checkBaseTileCompletion();
  }

  private markBaseTileComplete(tileInfo: TileInfo): void {
    if (!this.baseTileTracker) {
      return;
    }
    const tileKey = UrlBuilder.generateTileKey(tileInfo);
    if (!this.baseTileTracker.has(tileKey)) {
      return;
    }
    if (!this.baseTileTracker.get(tileKey)) {
      this.baseTileTracker.set(tileKey, true);
    }
    this.checkBaseTileCompletion();
  }

  private checkBaseTileCompletion(): void {
    if (!this.baseTileTracker) {
      return;
    }

    for (const isLoaded of this.baseTileTracker.values()) {
      if (!isLoaded) {
        return;
      }
    }

    this.resolveBaseTileTracking();
  }

  private resolveBaseTileTracking(force = false): void {
    if (!force && this.baseTileTracker) {
      for (const isLoaded of this.baseTileTracker.values()) {
        if (!isLoaded) {
          return;
        }
      }
    }

    if (this.baseTilesResolve) {
      this.baseTilesResolve();
      this.baseTilesResolve = undefined;
    }
    this.baseTilesPromise = undefined;
    this.baseTileTracker = null;

    if (this.isLoadingBaseTiles) {
      this.isLoadingBaseTiles = false;
      this.updateLoadingState();
    }
  }

  private async waitForBaseTilesCompletion(): Promise<void> {
    if (!this.baseTilesPromise) {
      return;
    }
    await this.baseTilesPromise;
  }

  private async loadBaseTile(tileInfo: TileInfo): Promise<void> {
    try {
      await this.loadQueue.enqueue([tileInfo]);
    } catch (error) {
      // Silently handle base tile loading errors
    }
  }
  private handleTileLoaded = (tileInfo: TileInfo): void => {
    this.markBaseTileComplete(tileInfo);
  };

  private handleTileLoadFailed = (tileInfo: TileInfo, _error: Error): void => {
    this.markBaseTileComplete(tileInfo);
  };

  private handleTileLoadCancelled = (tileInfo: TileInfo): void => {
    this.markBaseTileComplete(tileInfo);
  };

  /**
   * Update LOD level based on viewport (without loading tiles)
   */
  public updateLODForViewport(viewport: CameraViewport): boolean {
    // Update viewport (with throttling)
    const viewportUpdated = this.viewportService.updateViewport(viewport);
    if (!viewportUpdated) {
      return false; // Throttled
    }

    // Check if LOD level should change
    const lodChanged = this.lodController.evaluateViewport(viewport);
    // LOD level may have changed

    return lodChanged;
  }

  /**
   * Load tiles for the current camera viewport (debounced approach)
   */
  public async loadTilesForViewport(viewport: CameraViewport): Promise<void> {
    // Ensure metadata is available
    if (!this.config.metadata || !this.config.metadata.url || this.config.metadata.levels.length === 0) {
      throw new Error('Metadata configuration is required before loading tiles. Call updateMetadataConfig first.');
    }

    // Ensure base tiles are loaded first
    if (!this.isInitialized) {
      await this.initializeBaseTiles();
    }

    // Update LOD level first
    this.updateLODForViewport(viewport);

    // Always load the target level directly (skip intermediate levels)
    const targetLevel = this.lodController.getTargetLODLevel();
    const targetLODLevel = this.lodController.getLODLevelByNumber(targetLevel);

    if (!targetLODLevel) {
      return;
    }

    // Compute visible tiles for the target LOD level
    const visibleTilesResult = this.viewportService.computeVisibleTiles(targetLODLevel, this.config.metadata);

    // Boost priority for FOV tiles at highest LOD level
    const isHighestLevel = targetLevel === this.config.lodLevels[this.config.lodLevels.length - 1].level;
    if (isHighestLevel) {
      // Give FOV tiles at highest level very high priority
      for (const tile of visibleTilesResult.fovTiles) {
        tile.priority = LOADING.HIGHEST_LOD_FOV_PRIORITY + tile.priority;
      }
    }

    // Load FOV tiles first (high priority)
    if (visibleTilesResult.fovTiles.length > 0) {
      await this.loadQueue.enqueue(visibleTilesResult.fovTiles);

      // At highest LOD, wait for FOV tiles to complete before loading background tiles
      if (isHighestLevel) {
        // Wait a bit for FOV tiles to load (non-blocking for better UX)
        await new Promise(resolve => setTimeout(resolve, 300));
      }
    }

    // Then load non-FOV tiles (low priority, background)
    if (visibleTilesResult.nonFovTiles.length > 0) {
      // Don't await to avoid blocking interaction
      this.loadQueue.enqueue(visibleTilesResult.nonFovTiles);
    }

    // Tiles enqueued for target level
  }

  /**
   * Add LOD change callback
   */
  public addLODChangeCallback(callback: onLODChangeCallback): void {
    this.lodController.addLODChangeCallback(callback);
  }

  /**
   * Remove LOD change callback
   */
  public removeLODChangeCallback(callback: onLODChangeCallback): void {
    this.lodController.removeLODChangeCallback(callback);
  }

  /**
   * Get current cache statistics
   */
  public getCacheStats() {
    const cacheStats = this.textureCache.getStats();
    const spatialStats = this.spatialIndex.getStats();
    const loadStats = this.loadQueue.getStats();

    return {
      cacheSize: cacheStats.size,
      maxCacheSize: cacheStats.maxSize,
      memoryUsage: cacheStats.memoryEstimate,
      visibleTiles: spatialStats.visibleTiles,
      spatialAreas: spatialStats.spatialAreas,
      totalLoadedTiles: spatialStats.totalTiles,
      isInitialized: this.isInitialized,
      loadingSpatialAreas: loadStats.activeLoads,
      removedLevel1Faces: 0, // Legacy compatibility
      queueSize: loadStats.queueSize,
      activeLoads: loadStats.activeLoads,
      completedLoads: loadStats.completedLoads,
      failedLoads: loadStats.failedLoads,
    };
  }

  // NOTE: getCurrentLODLevel() moved earlier to provide summary shape.

  /**
   * Get visible tiles count
   */
  public getVisibleTilesCount(): number {
    return this.spatialIndex.getStats().visibleTiles;
  }

  /**
   * Get total loaded tiles count (across all spatial areas and levels)
   */
  public getPersistentTilesCount(): number {
    return this.spatialIndex.getStats().totalTiles;
  }

  public cancelAllLoads(): void {
    this.loadQueue.cancelAllLoads();
    this.pendingLoads = 0;
    this.updateLoadingState();
    this.resolveBaseTileTracking(true);
  }

  /**
   * Force reload all tiles
   */
  public async forceReload(clearPersistent: boolean = false): Promise<void> {

    if (clearPersistent) {
      // Clear everything including persistent tiles
      this.sceneAdapter.clearAllTileMeshes();
      this.textureCache.clear(true);
      this.spatialIndex.clear();
      this.cancelAllLoads();
      this.isInitialized = false;

      // Reinitialize base tiles
      if (this.config.metadata && this.config.metadata.url) {
        await this.initializeBaseTiles();
      }
    } else {
      // Only cancel current loads and reload viewport
      this.cancelAllLoads();
    }

    // Reload current viewport if available and metadata is valid
    const currentViewport = this.viewportService.getCurrentViewport();
    if (currentViewport && this.config.metadata && this.config.metadata.url && this.config.metadata.levels.length > 0) {
      await this.loadTilesForViewport(currentViewport);
    }
  }

  /**
   * Configure debug settings
   */
  public configureDebug(config: Partial<DebugConfig>): void {
    this.debug.configure(config);
  }

  // Legacy debug methods for API compatibility
  public debugTileStatus(): void {
    this.debug.debugTileStatus();
  }

  public debugSpatialKeyGeneration(): void {
    this.debug.debugSpatialKeyGeneration();
  }

  public debugCleanOverlappingTiles(): void {
    this.debug.debugCleanOverlappingTiles();
  }

  public debugSceneTileInfo(): void {
    this.debug.debugSceneTileInfo();
  }

  public debugRemoveLevel1ForFace(face: string): void {
    this.debug.debugRemoveLevel1ForFace(face);
  }

  /**
   * Get debug summary
   */
  public getDebugSummary(): string {
    return this.debug.getDebugSummary();
  }

  /**
   * Export debug data
   */
  public exportDebugData(): string {
    return this.debug.exportDebugData();
  }

  /**
   * Get performance analysis
   */
  public getPerformanceAnalysis() {
    return this.debug.getPerformanceAnalysis();
  }

  /**
   * Validate system integrity
   */
  public validateIntegrity(): { isValid: boolean; issues: string[] } {
    const issues: string[] = [];

    // Validate spatial index
    const spatialValidation = this.spatialIndex.validate();
    if (!spatialValidation.isValid) {
      issues.push(...spatialValidation.issues.map(issue => `SpatialIndex: ${issue}`));
    }

    // Validate scene consistency
    const sceneValidation = this.sceneAdapter.validateScene();
    if (!sceneValidation.isValid) {
      issues.push(...sceneValidation.issues.map(issue => `SceneAdapter: ${issue}`));
    }

    // Validate texture cache
    const cacheValidation = this.textureCache.validate();
    if (!cacheValidation.isValid) {
      issues.push(...cacheValidation.issues.map(issue => `TextureCache: ${issue}`));
    }

    // Validate LOD configuration
    const lodValidation = this.lodController.validateConfiguration();
    if (!lodValidation.isValid) {
      issues.push(...lodValidation.issues.map(issue => `LODController: ${issue}`));
    }

    // Validate replacement policy
    const policyValidation = this.replacementPolicy.validateConfig();
    if (!policyValidation.isValid) {
      issues.push(...policyValidation.issues.map(issue => `ReplacementPolicy: ${issue}`));
    }

    return {
      isValid: issues.length === 0,
      issues,
    };
  }

  /**
   * Get comprehensive system status
   */
  public getSystemStatus() {
    return {
      isInitialized: this.isInitialized,
      currentLOD: this.lodController.getCurrentLODLevel(),
      targetLOD: this.lodController.getTargetLODLevel(),
      transitionState: this.lodController.getTransitionState(),
      viewportStats: this.viewportService.getStats(),
      cacheStats: this.textureCache.getStats(),
      loadQueueStats: this.loadQueue.getStats(),
      spatialIndexStats: this.spatialIndex.getStats(),
      transitionStats: this.transitionCoordinator.getStats(),
      integrity: this.validateIntegrity(),
    };
  }

  /**
   * Update loading state and notify callbacks
   * - Base loading (isLoadingTextures): true when isLoadingBaseTiles is true (initial load)
   * - Any loading (anyLoading): true when pendingLoads > 0 (any tile loading)
   */
  private updateLoadingState(): void {
    const wasBaseLoading = this.isLoadingTextures;
    const isBaseLoading = this.isLoadingBaseTiles;

    if (wasBaseLoading !== isBaseLoading) {
      this.isLoadingTextures = isBaseLoading;

      if (isBaseLoading) {
        this.loadingStartTime = Date.now();
      }

      this.baseLoadingCallbacks.forEach(callback => {
        try {
          callback(isBaseLoading);
        } catch (error) {
          // Silently handle callback errors
        }
      });
    }

    const wasAnyLoading = this.anyLoading;
    const isAnyLoading = this.pendingLoads > 0;

    if (wasAnyLoading !== isAnyLoading) {
      this.anyLoading = isAnyLoading;
      this.anyLoadingCallbacks.forEach(callback => {
        try {
          callback(isAnyLoading);
        } catch (error) {
          // Silently handle callback errors
        }
      });
    }
  }

  /**
   * Add callback for loading state changes
   */
  public onLoadingStateChange(callback: (isLoading: boolean) => void): void {
    this.baseLoadingCallbacks.add(callback);
  }

  /**
   * Remove loading state change callback
   */
  public removeLoadingStateCallback(callback: (isLoading: boolean) => void): void {
    this.baseLoadingCallbacks.delete(callback);
  }

  /**
   * Get current loading state (only true during base tile initialization)
   */
  public isLoading(): boolean {
    return this.isLoadingBaseTiles;
  }

  public onAnyLoadingStateChange(callback: (isLoading: boolean) => void): void {
    this.anyLoadingCallbacks.add(callback);
  }

  public removeAnyLoadingStateCallback(callback: (isLoading: boolean) => void): void {
    this.anyLoadingCallbacks.delete(callback);
  }

  public isAnyLoading(): boolean {
    return this.anyLoading;
  }

  /**
   * Get number of pending texture loads
   */
  public getPendingLoadsCount(): number {
    return this.pendingLoads;
  }

  /**
   * Dispose of all resources
   */
  public dispose(): void {

    // Clear loading callbacks
    this.baseLoadingCallbacks.clear();
    this.anyLoadingCallbacks.clear();

    // Dispose services in reverse order of dependencies
    this.debug.dispose();
    this.transitionCoordinator.dispose();
    this.replacementPolicy.updateConfig({}); // No disposal needed
    this.viewportService.dispose();
    this.lodController.dispose();
    this.loadQueue.dispose();
    this.sceneAdapter.dispose();
    this.spatialIndex.clear();
    this.textureCache.dispose();

    // Reset state
    this.isInitialized = false;
    this.isLoadingTextures = false;
    this.pendingLoads = 0;
  }
}
